/* eslint-disable no-lone-blocks */
/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, {
  useEffect, useReducer, useRef, useState,
  forwardRef, useImperativeHandle,
} from 'react';
import Highcharts from 'highcharts';
import HighchartsExporting from 'highcharts/modules/exporting';
import { doc, onSnapshot } from 'firebase/firestore';
import db from '@services/firebase-service';
import { logToCloudWatch } from '@services/logger.service';
import {
  updateFreeFormPoll,
  getFreeFormPollComments
} from '@services/youtube-platform.service';
import { useStreamData } from '@lib/hooks/useStreamData';
import PollOption from '@components/Streams/PollOption';
import { pushDataLayerForEvent } from '@lib/gtag';

import { initialOptions, pollReducer } from '@components/Features/pollState/reducer';

import { handleEngagement, getOptionsName } from '@services/interactions-service';

import useFonts from '@lib/hooks/useFonts';
import useFontSizeCalculator from '@components/Presentation/helpers/fontSizeCalculator';

if (typeof Highcharts === 'object') {
  HighchartsExporting(Highcharts);
}

const Poll = forwardRef(({
  streamId, platformType, currentRosFeature, currentRosResult, interactionType, setCurrentFeatureId, startInteraction,
  splitDiv, dispatchTab, isFullScreen, setShowRosModel, interactionState,
  moderationModeRef, activeInteractionRef, updateFSMeta,
  setShowNotification, setStartTime,
  setTotalResponders
}, ref) => {
  const {
    firestoreUnsub,
    setFirestoreUnsub,
    setOverrideDismissStatus,
    setFeatureId
  } = useStreamData(s => ({
    firestoreUnsub: s.firestoreUnsub,
    setFirestoreUnsub: s.setFirestoreUnsub,
    setOverrideDismissStatus: s.setOverrideDismissStatus,
    setFeatureId: s.setFeatureId
  }));

  const { isResult, isRos } = interactionType;
  const [pollId, setPollId] = useState('');
  const [pollType, setPollType] = useState(currentRosFeature.type);
  const [pollColors, setPollColors] = useState(currentRosFeature.color);
  const [pollSettings, setPollSettings] = useState(currentRosFeature.setting);
  const [showProfilePictures, setShowProfilePictures] = useState(
    currentRosFeature.type === 'pollWithOptions' ?
      currentRosFeature?.setting?.pollWithOptionsShowProfilePictures ?? true
      : currentRosFeature?.setting?.pollShowProfilePictures ?? true
  );
  const [pollOptions, dispatch] = useReducer(pollReducer, initialOptions(currentRosFeature, currentRosResult, isResult));
  const pollOptionsRef = useRef(pollOptions);
  const [isDeleteUpdate, _setIsDeleteUpdate] = useState(false);
  const isDeleteUpdateRef = useRef(isDeleteUpdate);
  const setIsDeleteUpdate = (data) => {
    isDeleteUpdateRef.current = data;
    _setIsDeleteUpdate(data);
  };

  const [isPaused, _setIsPaused] = useState(false);
  const isPausedRef = useRef(isPaused);
  const setIsPaused = (data) => {
    isPausedRef.current = data;
    _setIsPaused(data);
  };

  const [exclusionList, _setExclusionList] = useState([]);
  const exclusionListRef = useRef(exclusionList);
  const setExclusionList = (data) => {
    exclusionListRef.current = [...exclusionListRef.current, ...data];
    _setExclusionList([...exclusionList, ...data]);
  };

  const { getFontStyle } = useFonts();
  const fontStyle = getFontStyle(pollSettings?.FontStyle || 'Sans-Serif');
  const fontSizeType = currentRosFeature?.type === 'pollWithOptions' ? 'pollWithOptionsFontSize' : 'pollFontSize';
  const computedFontSize = useFontSizeCalculator(pollSettings?.[fontSizeType] ? Number(pollSettings?.[fontSizeType]) : 1, 1.8, 'rem');

  const setupPoll = ({ id, settings, colors, options, noOfResponders }) => {
    setFeatureId(id);
    setCurrentFeatureId(id);
    setPollId(id);

    const interactionType = options.length > 1 ? 'pollWithOptions' : 'freeForm';
    setPollType(interactionType);

    setPollColors(colors);
    setPollSettings(settings);
    dispatch({ type: 'UPDATE_OPTION', options });

    setTotalResponders(noOfResponders);
  };

  const handlePoll = async ({ interactionId = null, interactionResult = [] }) => {
    const response = await handleEngagement({ streamId, platformType, currentRosFeature, interactionId });

    if (response.status && response.entity) {
      const interactionDetails = {
        id: response.entity.engagementId,
        type: currentRosFeature.type,
        settings: currentRosFeature.setting,
        colors: currentRosFeature.color,
        options: initialOptions(currentRosFeature, interactionResult, isResult),
        noOfResponders: response.entity.noOfResponders || 0
      };
      setupPoll(interactionDetails);
      updateFSMeta({ activeInteraction: interactionDetails });
    }
  };

  useEffect(() => {
    _setExclusionList([]);
    exclusionListRef.current = [];
  }, [pollId]);

  const deleteComment = async (text) => {
    setExclusionList(text);
    const res = await updateFreeFormPoll(pollId, { text, streamId, type: currentRosFeature.type });

    const updatedOptions = pollOptions.reduce((accumlator, option) => {
      if (exclusionListRef.current.includes(option.name)) {
        accumlator.push({ ...option, weight: 0 });
      } else {
        accumlator.push(option);
      }

      return accumlator;
    }, []);

    dispatch({ type: 'UPDATE_OPTION', options: updatedOptions });
    return res;
  };

  const unsubscribeFromFirestore = () => {
    if (firestoreUnsub.unsub) {
      firestoreUnsub.unsub();
    }
  };

  const getHighestCountComments = (dataList) => {
    if (dataList.length > 10) {
      return dataList.slice(0, 10);
    }
    return dataList;
  };

  useEffect(() => {
    if (currentRosFeature.color) {
      setPollColors(currentRosFeature.color);
    }
  }, [currentRosFeature.color]);

  useEffect(() => {
    if (currentRosFeature.setting) {
      setPollSettings(currentRosFeature.setting);
      setShowProfilePictures(
        currentRosFeature.type === 'pollWithOptions' ?
          currentRosFeature.setting?.pollWithOptionsShowProfilePictures ?? true
          : currentRosFeature.setting?.pollShowProfilePictures ?? true
      );
    }
  }, [currentRosFeature.setting]);

  useEffect(() => {
    if (currentRosFeature.options && currentRosFeature.type === 'pollWithOptions') {
      const updatedOptions = currentRosFeature.options.map(option => {
        const existingOption = (pollOptionsRef.current || []).find(
          pollOption => pollOption.name === option.body
        );

        return existingOption
          ? { ...existingOption }
          : { name: option.body, weight: 0, totalWeight: 0, count: 0, relativePercentage: 0, profileNames: [], otherCommenters: 0 };
      });

      dispatch({ type: 'UPDATE_OPTION', options: updatedOptions });
    }
  }, [currentRosFeature.options]);

  useEffect(() => {
    pollOptionsRef.current = pollOptions;
  }, [pollOptions]);

  useEffect(() => {
    if (pollId && isRos) {
      const logData = {
        streamId,
        interactionType: pollType,
        interactionId: pollId
      };
      setFirestoreUnsub({
        unsub: onSnapshot(doc(db, 'streams', streamId, 'poll', pollId), (document) => {
          if (document.exists() && !isPausedRef.current && !isDeleteUpdateRef.current) {
            if (currentRosFeature.type === 'freeForm') {
              const dataList = document.data().data.filter((x) => !exclusionListRef.current.includes(x.name));
              const comments = getHighestCountComments(dataList);

              if (comments.length > 0) setShowNotification(false);

              dispatch({ type: 'UPDATE_OPTION', options: comments });
              dispatchTab({ data: comments, type: 'UPDATE_TAB' });
            } else {
              const comments = document.data().data;

              if (comments.length > 0) setShowNotification(false);

              dispatch({
                type: 'UPDATE_OPTION',
                options: comments
              });
            }
            setTotalResponders(document.data().noOfResponders || 0);
          }
          setIsDeleteUpdate(false);
        }, (error) => logToCloudWatch('Failed to subscribe to firestore', { ...logData, error }, 'ERROR'))
      });
    }

    return () => {
      unsubscribeFromFirestore();
    };
  }, [pollId, isPausedRef.current]);

  useEffect(() => {
    if (activeInteractionRef.current?.id && activeInteractionRef.current?.type?.match(/(pollWithOptions|freeForm)/)) {
      setupPoll({
        id: activeInteractionRef.current.id,
        colors: activeInteractionRef.current.colors,
        settings: activeInteractionRef.current.settings,
        options: activeInteractionRef.current.options,
        noOfResponders: activeInteractionRef.current.noOfResponders || 0
      });
    }
  }, [activeInteractionRef.current]);

  const stopPoll = async () => {
    unsubscribeFromFirestore();
    // const sessionId = localStorage.getItem('sessionId');
    // await stopEngagement(streamId, currentRosFeature.type, { engagementId: pollId, platformType, sessionId });
  };

  const handleStartInteraction = () => {
    if (currentRosResult && currentRosResult.length > 0) {
      handlePoll({ interactionId: currentRosResult[0].id, interactionResult: currentRosResult });
    } else {
      handlePoll({ interactionId: null, interactionResult: [] });
    }
    setStartTime(Date.now());
  };

  const handleStopInteraction = () => {
    stopPoll();
    SendDataToGoogleAnalytics();
    setPollId('');
  };

  const managePollInteraction = () => {
    if (!isResult && startInteraction) {
      handleStartInteraction();
    } else if (pollId) {
      handleStopInteraction();
    } else if (currentRosResult && currentRosResult.length > 0) {
      setTotalResponders(currentRosResult[0].noOfResponders || 0);
    }
  };

  useEffect(() => {
    if (!currentRosFeature.id) return;

    setShowRosModel(false);
    if (moderationModeRef.current) return;

    managePollInteraction();

    setOverrideDismissStatus(new Date().getTime());
  }, [currentRosFeature.id, startInteraction]);

  const getFreeFormPollProcessedCommentsCount = async () => {
    const response = await getFreeFormPollComments(pollId);
    if (response && response.status && response.entity.processedComments.length > 0) {
      pushDataLayerForEvent('end_power_poll_interaction', { no_comments: response.entity.processedComments.length });
    }
  };

  const getPollWithOptionsProcessedCommentsCount = () => pushDataLayerForEvent('end_power_poll_interaction', { no_comments: pollOptions[0]?.totalWeight }); // seems incorrect

  const SendDataToGoogleAnalytics = () => {
    pollType === 'pollWithOptions' ? getPollWithOptionsProcessedCommentsCount() : getFreeFormPollProcessedCommentsCount();
  };

  const handleEditBtn = () => {
    if (isPaused) {
      setIsPaused(false);
    } else {
      setIsPaused(true);
    }
  };

  useImperativeHandle(ref, () => ({
    handleEditBtn,
    deleteComment,
    async triggerEngagement({ interactionResult }) {
      if (interactionResult && interactionResult.length > 0) {
        await handlePoll({ interactionId: interactionResult[0].id, interactionResult });
      }
    }
  }));

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [isFullScreen]);

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [splitDiv]);

  return (
    <>
      <div className={'h-full overflow-hidden '}>
        <div className={'relative overflow-hidden rounded-lg w-full h-full'}>
          {fontStyle && pollOptions && pollColors && <div className='w-full h-full relative'>
            <div className='w-full h-full flex py-2 gap-4 flex-col justify-center'>
              {
                pollOptions.map((option, index) => <PollOption
                  key={option.name}
                  optionNumber={`${getOptionsName(currentRosFeature, index)}`} name={`${option.name}`} id={index} option={option} totalCount={pollOptions.reduce((sum, obj) => sum + obj.count, 0)} count={option.count || 0} weight={option.weight || 0} relativePercentage={option.relativePercentage || 0} color={pollColors.pollChartColor}
                  currentRosFeature={currentRosFeature} pollOptions={pollOptions} pollColors={pollColors} pollSettings={pollSettings}
                  fontFamily={fontStyle}
                  fontSize={computedFontSize}
                  profileNames={option?.profileNames || []}
                  otherCommenters={option?.otherCommenters || 0}
                  showProfilePictures={showProfilePictures}
                />)
              }
            </div>
          </div>}

        </div>
      </div >
    </>
  );
});

export default Poll;
